// src/context/brandVoice/voiceModel.js — the "Personalità del brand" voice, as data. PURE.
//
// ⭐ v190. The voice is NOT a field of the brand-guidelines document: it has its own route
// (GET|PUT /context/brand-voice[?language=en]), its own etag PER LANGUAGE and ONE lock for
// the whole form (the document read's `voice` block). This module holds every rule the voice
// form needs, so no component re-derives one:
//   - the problem keys `voice.<lang>.<key>` (so the page can open the right tab AND language);
//   - the rows a language shows — ⛔ exactly the axes the server returned, in its order: the
//     vocabulary is closed server-side and an unknown key is dropped in silence, so the list
//     is never hard-coded here (decision 8: no add / remove of axes);
//   - the English "stored" value when the workspace has NO English file (the read is a 404):
//     the Italian keys, everything empty — so an untouched English form is not a change;
//   - the browser checks (decision 9) and refusal B;
//   - the PUT body.
// ⛔ No React, no network, no copy: it returns i18n KEYS, never a sentence.
import { voiceDraftFromRead } from './fieldValue.js';

export const VOICE_LANGS = ['it', 'en'];

/** The problem / focus key of one voice value: `voice.it.label`, `voice.en.warmth`. */
export const voicePath = (lang, key) => `voice.${lang}.${key}`;

/** 'it' | 'en' for a voice problem key, else null (a document field path). */
export function voiceLangOf(path) {
  const match = /^voice\.(it|en)\./.exec(typeof path === 'string' ? path : '');
  return match ? match[1] : null;
}

const axesOf = (read) => (read && Array.isArray(read.axes) ? read.axes : []);

/**
 * The rows one language's form shows — `{ axis, label }` in the SERVER's order. English with
 * no English file (`enRead` null) → the Italian axes AND their Italian labels: a 404 carries no
 * labels, and the axis list must not be hard-coded.
 */
export function voiceAxes(lang, itRead, enRead) {
  const source = lang === 'en' && enRead ? enRead : itRead;
  return axesOf(source)
    .filter((a) => a && typeof a.axis === 'string' && a.axis)
    .map((a) => ({ axis: a.axis, label: typeof a.label === 'string' && a.label ? a.label : a.axis }));
}

/** The English "stored" value of a workspace with no English file: Italian keys, all empty. */
export function emptyVoiceFrom(itRead) {
  if (!itRead) return null;
  return {
    label: '',
    axes: Object.fromEntries(axesOf(itRead)
      .filter((a) => a && typeof a.axis === 'string' && a.axis)
      .map((a) => [a.axis, ''])),
  };
}

/** The value a language's draft is compared against (the "stored" voice, editor shape). */
export function voiceOriginal(lang, itRead, enRead) {
  if (lang === 'en') return enRead ? voiceDraftFromRead(enRead) : emptyVoiceFrom(itRead);
  return voiceDraftFromRead(itRead);
}

const blank = (v) => typeof v !== 'string' || v.trim() === '';

/**
 * Decision 9: the name and EVERY shown axis must hold text (the backend refuses a blank
 * guidance or label, in English and with no path). → `{ [voicePath]: [{ key }] }`, keys into
 * `brandVoice.validation.*`.
 */
export function voiceProblems(lang, value) {
  if (!value) return {};
  const out = {};
  if (blank(value.label)) out[voicePath(lang, 'label')] = [{ key: 'required' }];
  Object.entries(value.axes || {}).forEach(([axis, guidance]) => {
    if (blank(guidance)) out[voicePath(lang, axis)] = [{ key: 'required' }];
  });
  return out;
}

/**
 * Decision B: an English change with NO Italian change in the same save, on a workspace still
 * reading the platform's voice (`source: 'global'`), is refused here — the backend would answer
 * 409 CONTEXT_BASE_REQUIRED (no English file without the Italian one).
 */
export const italianFirstRefusal = ({ itDirty, enDirty, itSource }) => (
  !!enDirty && !itDirty && itSource === 'global'
);

/** The PUT body `{ label, axes }`: trimmed, exactly the axes shown (⛔ none added, none dropped). */
export function voicePayload(value) {
  const trim = (v) => (typeof v === 'string' ? v.trim() : '');
  return {
    label: trim(value?.label),
    axes: Object.fromEntries(Object.entries(value?.axes || {}).map(([axis, guidance]) => [axis, trim(guidance)])),
  };
}
